import { useEffect, useState } from 'react'

import { ApiError, api } from '../api'
import { SEAL_GLYPHS, Seal } from '../components/Seal'
import type { SealSlotId } from '../components/Seal'
import { SystemIcon } from '../components/SystemIcon'
import type { SystemId } from '../components/SystemIcon'
import { haptic } from '../telegram'
import './firstLook.css'

interface Coordinate {
  system: SystemId
  label: string
  glyph?: string
  color: string
}

interface Crossing {
  kind: 'support' | 'contradiction' | 'condition' | 'unclear'
  title: string
  evidence: { system: SystemId; label: string; detail?: string }[]
}

export interface AtlasResponse {
  seal: {
    coreGlyph: string | null
    slots: { id: SealSlotId; label: string; note?: string; color: string; sealed: boolean }[]
  }
  coordinates: Coordinate[]
  crossing: Crossing | null
  tension: Crossing | null
  limitations: { code: string; message: string }[]
}

const KIND_TITLES: Record<Crossing['kind'], string> = {
  support: 'Первое пересечение',
  contradiction: 'Первое противоречие',
  condition: 'Это работает не всегда',
  unclear: 'Пока неясно',
}

/**
 * Первый настоящий результат: на глазах собирается личная печать, появляются
 * три координаты и одна мысль, которую независимо подтвердили разные карты.
 * До этого экрана продукт ничего не продаёт и ничего не просит.
 */
export function FirstLook({ onOpen }: { onOpen: () => void }) {
  const [data, setData] = useState<AtlasResponse | null>(null)
  const [error, setError] = useState('')
  /** Шаги сборки: печать → координаты → пересечение. */
  const [stage, setStage] = useState(0)

  useEffect(() => {
    let cancelled = false
    api
      .get<AtlasResponse>('/api/atlas')
      .then((result) => {
        if (cancelled) return
        setData(result)
        haptic('medium')
      })
      .catch((cause: unknown) => {
        if (cancelled) return
        setError(cause instanceof ApiError ? cause.message : 'Не получилось собрать печать')
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!data || stage >= 3) return undefined
    const timer = window.setTimeout(() => setStage((current) => current + 1), stage === 0 ? 900 : 700)
    return () => window.clearTimeout(timer)
  }, [data, stage])

  if (error) return <div className="state">{error}</div>
  if (!data) return <div className="state">Собираем твою печать…</div>

  const crossing = data.crossing ?? data.tension
  const secondary = data.crossing && data.tension ? data.tension : null

  return (
    <div className="screen no-tabs first-look">
      <div className="topbar">
        <div className="brand">АТЛАС</div>
        <div className="topnote">шаг 3 из 3</div>
      </div>

      <h1 className="display">Твоя печать собрана</h1>
      <div className="sub">
        Карты уже прочитаны. Печать — это ты, сложенный из их символов. Дальше она будет расти.
      </div>

      <div className={`seal-stage${stage >= 1 ? ' shown' : ''}`}>
        <Seal
          coreGlyph={data.seal.coreGlyph ?? undefined}
          slots={data.seal.slots.map((slot) => ({
            id: slot.id,
            label: slot.label,
            ...(slot.note ? { note: slot.note } : {}),
            color: slot.color,
            sealed: slot.sealed,
            glyph: SEAL_GLYPHS[slot.id](slot.color),
          }))}
        />
      </div>

      <div className={`chips coords${stage >= 2 ? ' shown' : ''}`}>
        {data.coordinates.map((coordinate) => (
          <span className="chip" key={coordinate.label}>
            <span className="dot" style={{ background: coordinate.color }} />
            {coordinate.label}
            {coordinate.glyph && (
              <span className="cjk" style={{ color: coordinate.color }}>
                &nbsp;{coordinate.glyph}
              </span>
            )}
          </span>
        ))}
      </div>

      {crossing && (
        <div className={`card cross-card${stage >= 3 ? ' shown' : ''}`}>
          <div className="lbl" style={{ color: 'var(--gold)' }}>
            {KIND_TITLES[crossing.kind]}
          </div>
          <p className="cross-title">{crossing.title}</p>
          <div className="cross-bases">
            {crossing.evidence.slice(0, 3).map((item) => (
              <div className="base-row" key={`${item.system}-${item.label}`}>
                <SystemIcon system={item.system} />
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {secondary && stage >= 3 && (
        <div className="card cross-card shown tension-card">
          <div className="lbl">{KIND_TITLES[secondary.kind]}</div>
          <p className="cross-title">{secondary.title}</p>
        </div>
      )}

      {data.limitations.length > 0 && stage >= 3 && (
        <div className="limitations">
          {data.limitations.map((limitation) => (
            <div className="limitation" key={limitation.code}>
              {limitation.message}
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        className={`btn btn-gold cta${stage >= 3 ? ' shown' : ''}`}
        onClick={() => {
          haptic('medium')
          onOpen()
        }}
      >
        Открыть Атлас
      </button>
      <div className="hint">
        Дальше Атлас раскрывается по одной странице.
        <br />
        Без таймеров и спешки.
      </div>
    </div>
  )
}
